import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { textProp, type ElementProps } from './elementProps';
import { ELEMENT_FALLBACK_ICON, ELEMENT_ICONS } from './elementIcons';
import './elementTabs.css';
import { useShopNames } from '../../commerce/shopNames';

// A strip of names and one panel at a time. The panels are the children, in order; the names are the
// `tabs` list. A name with no panel is not drawn, and a panel with no name is called by its number.
//
// The strip is a real tablist: arrow keys move along it, Home and End jump, and each name says which
// panel it controls, so a screen reader hears "tab 2 of 3, Best sellers" and not three buttons.

/** One name of the strip, read out of the `tabs` list, and the sentence a side-placed tab shows under it. */
type TabName = { label: string; description: string };

// Reads the names, dropping anything that is not one — the schema stores a list open.
function readNames(props: ElementProps): TabName[] {
  const raw = props.tabs;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    if (typeof row !== 'object' || row === null) return [];
    const { label, description } = row as Record<string, unknown>;
    return typeof label === 'string' ? [{ label, description: typeof description === 'string' ? description : '' }] : [];
  });
}

// Which panel a key press lands on, walking the strip and wrapping at either end.
function nextIndex(key: string, at: number, count: number): number | null {
  if (key === 'ArrowRight' || key === 'ArrowDown') return (at + 1) % count;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return (at - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

// Draws the strip of names, once for the page and once, inert, over every stacked panel in the builder.
const TabStrip: React.FC<{
  id: string; names: TabName[]; active: number; side: boolean; onPick: (at: number) => void;
}> = ({ id, names, active, side, onPick }) => (
  <div className="xe-tabs-strip" role="tablist">
    {names.map((name, at) => (
      <button
        key={at}
        type="button"
        role="tab"
        id={`${id}-tab-${at}`}
        aria-selected={at === active}
        aria-controls={`${id}-panel-${at}`}
        tabIndex={at === active ? 0 : -1}
        className="xe-tabs-tab"
        data-active={at === active ? '' : undefined}
        onClick={() => onPick(at)}
        onKeyDown={(event) => {
          const to = nextIndex(event.key, at, names.length);
          if (to === null) return;
          event.preventDefault();
          onPick(to);
          document.getElementById(`${id}-tab-${to}`)?.focus();
        }}
      >
        <span className="xe-tabs-name">{name.label || `Tab ${at + 1}`}</span>
        {side && name.description && <span className="xe-tabs-description">{name.description}</span>}
      </button>
    ))}
  </div>
);

/** The chevron drawn over the phone's tab picker, which a styled native select no longer draws itself. */
const Chevron = ELEMENT_ICONS['chevron-down'] ?? ELEMENT_FALLBACK_ICON;

// Draws the phone's tab picker: a native select in Sleek's pill, and the chosen tab's sentence under it.
const TabChoice: React.FC<{ names: TabName[]; current: number; onPick: (at: number) => void }> = ({ names, current, onPick }) => {
  const said = useShopNames();
  return (
    <>
      <div className="xe-tabs-choice">
        <select className="xe-tabs-select" aria-label={said.chooseTab} value={current} onChange={(event) => onPick(Number(event.target.value))}>
          {names.map((name, index) => <option key={index} value={index}>{name.label || `Tab ${index + 1}`}</option>)}
        </select>
        <Chevron size={18} strokeWidth={1.5} aria-hidden="true" className="xe-tabs-chevron" />
      </div>
      {names[current]?.description ? <p className="xe-tabs-choice-note" dir="auto">{names[current].description}</p> : null}
    </>
  );
};

// Opens, in the builder, the tab holding whatever element the merchant just picked in the tree.
function useRevealedTab(editing: boolean, host: React.RefObject<HTMLDivElement>, setActive: (at: number) => void): void {
  useEffect(() => {
    if (!editing) return;
    // Opens the tab holding whatever the builder selected, so the merchant sees what they picked.
    const reveal = (event: Event) => {
      const mark = document.querySelector(`[data-xshop-el="${CSS.escape((event as CustomEvent<string>).detail)}"]`);
      const panels = host.current?.querySelectorAll('[data-tab-index]');
      panels?.forEach((panel) => {
        if (mark && panel.contains(mark) && panel.parentElement?.parentElement === host.current) setActive(Number(panel.getAttribute('data-tab-index')));
      });
    };
    document.addEventListener('xshop:reveal-element', reveal);
    return () => document.removeEventListener('xshop:reveal-element', reveal);
  }, [editing, host, setActive]);
}

// Follows the open tab and every tab opened so far: a shopper's panel is drawn the first time it opens and kept after,
// so a page with three tabs of products lays out and reads one, and a panel never loses what was typed into it.
function useOpenedTabs(): { active: number; opened: ReadonlySet<number>; pick: (at: number) => void } {
  const [active, setActive] = useState(0);
  const [opened, setOpened] = useState<ReadonlySet<number>>(() => new Set([0]));
  const pick = useCallback((at: number) => {
    setActive(at);
    setOpened((was) => (was.has(at) ? was : new Set(was).add(at)));
  }, []);
  return { active, opened, pick };
}

// Draws the strip and the panels: one shown for a shopper, all of them stacked for the builder.
export const TabsElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing } = useElementRender();
  const id = useId();
  const { active, opened, pick } = useOpenedTabs();
  const host = useRef<HTMLDivElement>(null);
  useRevealedTab(editing, host, pick);
  const panels = React.Children.toArray(children);
  const authoredNames = readNames(node.props);
  const names = panels.map((_panel, at) => authoredNames[at] ?? { label: `Tab ${at + 1}`, description: '' });
  const placement = textProp(node.props, 'placement') === 'side' ? 'side' : 'top';
  const current = Math.min(active, Math.max(panels.length - 1, 0));


  return (
    <div ref={host} {...box} className={[box.className, 'xe-tabs'].filter(Boolean).join(' ')} data-placement={placement}>
      <div className={editing ? 'xs-editor-chrome xe-tabs-navigation' : 'xe-tabs-navigation'}>
      <TabChoice names={names} current={current} onPick={pick} />
      <TabStrip
        id={id}
        names={names.length ? names : panels.map((_p, at) => ({ label: `Tab ${at + 1}`, description: '' }))}
        active={current}
        side={placement === 'side'}
        onPick={pick}
      />
      </div>
      <div className="xe-tabs-panels">
        {panels.map((panel, at) => (
          <div
            key={at}
            data-tab-index={at}
            role="tabpanel"
            id={`${id}-panel-${at}`}
            aria-labelledby={`${id}-tab-${at}`}
            className="xe-tabs-panel"
            hidden={at !== current}
          >
            {editing || opened.has(at) ? panel : null}
          </div>
        ))}
      </div>
    </div>
  );
};
